import { Link } from 'react-router-dom';

const services = [
  {
    icon: 'ri-file-text-line',
    title: 'Visa Application Assistance',
    description: 'Complete support for preparing and submitting your visa application. Our experts guide you through the entire process, ensuring all forms are correctly filled and all requirements are met.',
  },
  {
    icon: 'ri-award-line',
    title: 'Visa Accreditation Support',
    description: 'Assistance with visa accreditation requirements for businesses, NGOs, and organisations that need to register with embassies for regular visa processing.',
  },
  {
    icon: 'ri-calendar-check-line',
    title: 'Appointment Booking Assistance',
    description: 'We handle embassy and visa application centre appointment scheduling on your behalf, securing the earliest available dates and managing rescheduling when needed.',
  },
  {
    icon: 'ri-file-search-line',
    title: 'Document Review and Verification',
    description: 'Thorough review of all your supporting documents against embassy requirements. We identify gaps or issues before submission, reducing the risk of rejection.',
  },
  {
    icon: 'ri-passport-line',
    title: 'Passport and Supporting Document Handling',
    description: 'Secure handling of your passport and all original documents. We manage document submission to embassies and collection after processing with full chain of custody.',
  },
  {
    icon: 'ri-home-office-line',
    title: 'Apply From Home Service',
    description: 'Complete your visa application without leaving your home. Our team comes to you for document collection, handles everything, and delivers your passport back.',
  },
  {
    icon: 'ri-truck-line',
    title: 'Document Collection Service',
    description: 'We collect your documents from your home, office, or preferred location in Lusaka. After processing, we deliver your passport and documents back to you.',
  },
  {
    icon: 'ri-timer-line',
    title: 'Application Tracking',
    description: 'Real-time online tracking of your visa application using your unique PVS reference number. Monitor your application progress at every stage.',
  },
  {
    icon: 'ri-plane-line',
    title: 'Travel Documentation Support',
    description: 'Assistance with travel-related documentation including travel insurance recommendations, flight itineraries, hotel bookings, and invitation letter preparation.',
  },
  {
    icon: 'ri-building-4-line',
    title: 'Corporate and Group Visa Support',
    description: 'Specialised visa processing for companies, organisations, and groups. We handle bulk applications, business delegations, and corporate travel requirements.',
  },
  {
    icon: 'ri-government-line',
    title: 'Embassy / Visa Centre Submission Support',
    description: 'We represent you at embassy submissions, ensuring your application package is properly presented and all embassy-specific protocols are followed.',
  },
  {
    icon: 'ri-inbox-line',
    title: 'Passport Collection and Delivery Updates',
    description: 'We monitor your application status and notify you immediately when your passport is ready. We then collect and deliver it to your preferred location.',
  },
];

export default function VisaServices() {
  return (
    <div>
      <section className="relative h-[280px] md:h-[350px] flex items-center justify-center overflow-hidden">
        <img
          src="https://readdy.ai/api/search-image?query=Elegant%20dark%20navy%20background%20with%20subtle%20passport%20stamps%20and%20visa%20stickers%20pattern%2C%20warm%20orange%20accent%20highlights%2C%20professional%20corporate%20style%2C%20clean%20modern%20design%2C%20travel%20document%20theme&width=1920&height=600&seq=visa-services-premium&orientation=landscape"
          alt="Visa Services - Premium Visa Solutions"
          className="absolute inset-0 w-full h-full object-cover object-top"
          title="Our Visa Services - Premium Visa Solutions"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-primary-950/75 to-primary-950/65"></div>
        <div className="relative z-10 text-center px-4">
          <h1 className="font-heading text-3xl md:text-5xl font-bold text-background-50 mb-3">Visa Services</h1>
          <p className="text-sm md:text-base text-background-50/80 max-w-[600px] mx-auto">
            Comprehensive visa support services designed to make your application process smooth and stress-free
          </p>
        </div>
      </section>

      <section className="py-14 md:py-20 bg-background-50">
        <div className="w-full max-w-[1280px] mx-auto px-4 md:px-8 lg:px-12">
          <div className="text-center mb-12">
            <span className="inline-block bg-accent-100 text-accent-700 text-xs font-semibold px-3 py-1 rounded-full mb-3">What We Offer</span>
            <h2 className="font-heading text-2xl md:text-4xl font-bold text-foreground-900 mb-3">
              Complete Visa Support Services
            </h2>
            <p className="text-sm md:text-base text-foreground-600 max-w-[650px] mx-auto">
              From application preparation to passport delivery, we offer end-to-end visa support for individuals, families, and organisations.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
            {services.map((service, idx) => (
              <div
                key={idx}
                className="bg-background-50 border border-secondary-200 rounded-lg p-5 md:p-6 hover:border-primary-300 transition-all duration-200 group cursor-pointer"
              >
                <div className="w-11 h-11 md:w-12 md:h-12 flex items-center justify-center bg-primary-100 rounded-lg mb-4 group-hover:bg-primary-500 transition-colors duration-200">
                  <i className={`${service.icon} text-primary-600 group-hover:text-background-50 text-lg md:text-xl transition-colors duration-200`}></i>
                </div>
                <h3 className="font-heading font-semibold text-base md:text-lg text-foreground-900 mb-2">{service.title}</h3>
                <p className="text-sm text-foreground-600 leading-relaxed">{service.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-14 md:py-20 bg-primary-900">
        <div className="w-full max-w-[1280px] mx-auto px-4 md:px-8 lg:px-12 text-center">
          <h2 className="font-heading text-2xl md:text-4xl font-bold text-background-50 mb-4">
            Ready to Start Your Application?
          </h2>
          <p className="text-sm md:text-base text-background-50/70 max-w-[600px] mx-auto mb-8">
            Choose our Apply From Home service and let us handle everything while you relax. Your visa journey starts here.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
            <Link
              to="/apply-from-home"
              className="w-full sm:w-auto px-10 py-3.5 bg-accent-500 text-background-50 font-semibold rounded-md hover:bg-accent-600 transition-colors text-sm whitespace-nowrap cursor-pointer"
            >
              Apply From Home
            </Link>
            <Link
              to="/contact-us"
              className="w-full sm:w-auto px-10 py-3.5 bg-background-50/10 text-background-50 font-semibold rounded-md border border-background-50/30 hover:bg-background-50/20 transition-colors text-sm whitespace-nowrap cursor-pointer"
            >
              Speak to Our Team
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}